/**
 * LagRootCauseOverlayContent — single-sentence diagnostic banner.
 *
 * Polls every 500ms (cheap — pure read of cached signals). Surfaces the
 * top-line verdict from LagRootCauseAnalyzer with a colored badge for
 * severity. Click to expand details (which signal triggered, raw numbers).
 *
 * Phase H.8 (2026-05-12). The arc's synthesis tool — DJ can leave this
 * overlay up during gameplay and glance to know "what's wrong right now"
 * without scanning four separate panels.
 */

import React, { useEffect, useState } from 'react';
import { analyzeLag, type LagDiagnosis } from '../../../diagnostics/LagRootCauseAnalyzer';
import { useColyseusMessages } from '../../hooks/useColyseusMessages';
import type { TickHealthSnapshot } from '../../../../shared/diagnostics/TickHealthTypes';
import { CopyButton } from '../CopyButton';

const POLL_INTERVAL_MS = 500;

const SEVERITY_COLOR: Record<LagDiagnosis['severity'], string> = {
  healthy: '#42d77d',
  mild: '#fa5',
  severe: '#ff5f5f',
  // Grey, never green: no inputs is the absence of a verdict, not a good one.
  unknown: '#f1c40f',
};

const SEVERITY_ICON: Record<LagDiagnosis['severity'], string> = {
  healthy: '🟢',
  mild: '🟡',
  severe: '🔴',
  unknown: '⚠️',
};

function readSpikeTier(): string | undefined {
  // NetSpikeAnalyzer doesn't expose its tier as a global, but it sets
  // data-tier on its own panel element. Read it from the DOM if the
  // panel is mounted. Returns undefined if NetSpike isn't open.
  try {
    const el = document.querySelector('[data-testid="net-spike-analyzer"]');
    return el?.getAttribute('data-tier') ?? undefined;
  } catch {
    return undefined;
  }
}

export const LagRootCauseOverlayContent: React.FC = () => {
  const [diagnosis, setDiagnosis] = useState<LagDiagnosis | null>(null);
  const [tickHealth, setTickHealth] = useState<TickHealthSnapshot | null>(null);
  const [expanded, setExpanded] = useState(false);

  useColyseusMessages({
    'netobs:tick': (snap: TickHealthSnapshot) => setTickHealth(snap),
  });

  useEffect(() => {
    const poll = () => {
      const spikeTier = readSpikeTier();
      setDiagnosis(analyzeLag({ tickHealth, spikeTier }));
    };
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [tickHealth]);

  if (!diagnosis) return null;
  const color = SEVERITY_COLOR[diagnosis.severity];

  return (
    <div
      data-testid="lag-root-cause-overlay"
      data-severity={diagnosis.severity}
      data-category={diagnosis.category}
      data-summary={diagnosis.summary}
      /* Headless readback: a verdict is only worth the inputs behind it. */
      data-inputs-available={diagnosis.inputsAvailable}
      data-detail-message-type={diagnosis.details.messageType as string | undefined}
      data-detail-max-bytes={diagnosis.details.maxBytes as number | undefined}
      data-detail-worst-subsystem={diagnosis.details.worstSubsystem as string | undefined}
      data-detail-snaps-last-10s={diagnosis.details.snapsLast10s as number | undefined}
      style={{
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
        padding: '6px 8px',
        userSelect: 'none',
      }}
    >
      <div style={{ color: '#7af', marginBottom: 4, letterSpacing: '0.05em', fontWeight: 600, fontSize: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>LAG ROOT CAUSE · polled {POLL_INTERVAL_MS}ms</span>
        <CopyButton getText={() => {
          const lines = [
            `LAG ROOT CAUSE @ ${new Date().toISOString()}`,
            `severity: ${diagnosis.severity}`,
            `category: ${diagnosis.category}`,
            `summary:  ${diagnosis.summary}`,
            `inputs:   ${diagnosis.inputsAvailable}/3 reporting`,
            'details:',
            ...Object.entries(diagnosis.details).map(([k, v]) => `  ${k}: ${v}`),
          ];
          return lines.join('\n');
        }} />
      </div>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: `1px solid ${color}`,
          borderRadius: 3,
          padding: '6px 8px',
          color,
          fontFamily: 'inherit',
          fontSize: 11,
          cursor: 'pointer',
        }}
        title={expanded ? 'collapse details' : 'click for details'}
      >
        <span style={{ fontWeight: 700 }}>{SEVERITY_ICON[diagnosis.severity]}</span>{' '}
        {diagnosis.summary}
      </button>
      {expanded && (
        <div style={{ marginTop: 4, fontSize: 9, color: '#888' }}>
          <div>category: <span style={{ color: '#ccc' }}>{diagnosis.category}</span></div>
          {Object.entries(diagnosis.details).map(([k, v]) => (
            v !== undefined && (
              <div key={k}>
                {k}: <span style={{ color: '#ccc' }}>{typeof v === 'number' ? v.toFixed(2) : String(v)}</span>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
};
